import React, { useContext, useCallback, useState, useMemo, useEffect } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Checkbox } from '@fluentui/react';
import { AccordionHeader, getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { StepContainer } from '../step-container/step-container';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { processPayload, saveGoal, getErrorMessage } from '../../conversion-goals-repository';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';
import { endActivity } from '../../shared/util';

const SmartGoalDetailUnstyled = (props) => {
  const {
    classes,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const { logActivity } = wizardContext;
  const [isMainGoalChecked, setIsMainGoalChecked] = useState(wizardContext.goalContext.IsMainConversionGoal);
  const [isExpanded, setIsExpanded] = useState(true);
  const [notificationError, setNotificationError] = useState(null);
  const [footerEnabled, setFooterEnabled] = useState(true);

  const onChangeMainGoalSelection = useCallback((event, value) => {
    logActivity.trace(`AdvancedGoalSettings:SmartGoal:Setting isMainGoal to ${value}`);
    setIsMainGoalChecked(value);
  }, [logActivity]);

  const toggle = useCallback(() => {
    if (!isExpanded) {
      logActivity.trace('AdvancedGoalSettings:SmartGoal:Expanded toggle for CountAsConversions');
    }
    setIsExpanded(!isExpanded);
  }, [isExpanded, logActivity]);

  const goalDelta = {
    IsMainConversionGoal: isMainGoalChecked,
  };

  const handleNext = useCallback(() => {
    // go to completion page
    saveGoal(processPayload({ ...wizardContext.goalContext, ...goalDelta }, wizardContext.permissions), wizardContext).then((response) => {
      endActivity(logActivity);
      wizardContext.dispatch({
        type: WIZARD_ACTIONS.NAVIGATE_TO_LAST_STEP,
        data: { ...goalDelta, Id: response.Id },
      });
    }).catch((response) => {
      setFooterEnabled(true);
      setNotificationError(getErrorMessage(response, i18n));
    });
    setFooterEnabled(false);
  }, [wizardContext, goalDelta, logActivity, i18n]);

  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notificationError)) {
      notificationId = notificationApi.showWarning(notificationError);
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notificationError]);

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Secondary',
      onClick: wizardContext.goBack,
      isEnabled: false,
      text: i18n.getString(_TL_('Back')),
    },
    {
      type: 'Primary',
      onClick: handleNext,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Save and next')),
    },
    {
      type: 'Cancel',
      onClick: wizardContext.cancel,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Cancel')),
    }],
  }), [wizardContext.goBack, wizardContext.cancel, i18n, handleNext, footerEnabled]);

  return (
    <StepContainer
      title={i18n.getString(_TL_('Edit Smart goal'))}
      step="goal-details-smart"
      footerConfig={footerConfig}
    >
      <div className={classes.contentBlock}>
        {/* main goal selection */}
        <div className={classNames(classes.accordionBlock, classes.mainGoalBlock)}>
          <AccordionHeader
            expanded={isExpanded}
            onChange={() => toggle('mainGoal')}
            helpId="pop_BA_ConversionGoal_CountAsConv"
            i18n={i18n}
          >
            {i18n.getString(_TL_('Include in "Conversions"'))}
          </AccordionHeader>
          <div>
            {isExpanded ?
              (
                <Checkbox
                  label={i18n.getString(_TL_('These conversions will appear in the "Conversions" column in the data table and will also affect any automated bidding bid strategies you have enabled.'))}
                  checked={isMainGoalChecked}
                  onChange={onChangeMainGoalSelection}
                />
              ) :
              (
                <p>{isMainGoalChecked ? 'Yes' : 'No'}</p>
              )}
          </div>
        </div>
      </div>
    </StepContainer>
  );
};

SmartGoalDetailUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
};

export const SmartGoalDetail = withPerfMarker(withDefaultStyles(SmartGoalDetailUnstyled, getStepStyles));
